/* eslint-disable @next/next/no-img-element */
/**
 * Q-SUITE platform illustration, reconstructed layer-by-layer from the Figma
 * "Q-SUITE Animation" component (node 2179:55874). The gears and the pie chart
 * are animated (slow rotation) to match the design's frame variants; everything
 * else is the static illustration. Assets live in /images/Product/qsuite-anim/.
 */

const BASE = "/images/Product/qsuite-anim";
const a = (hash: string) => `${BASE}/${hash}.svg`;

interface QSuiteAnimationProps {
  className?: string;
}

export default function QSuiteAnimation({ className }: QSuiteAnimationProps) {
  return (
    <div
      className={className ?? "relative w-full aspect-[638/436] overflow-hidden"}
      role="img"
      aria-label="ECQ Q-SUITE offensive security platform illustration"
    >
      <div className="absolute contents inset-[0.08%_0_-0.01%_0]">
        {/* Big circular disc (masked base) */}
        <div className="absolute contents inset-[2.21%_35.43%_0.5%_11.33%]">
          <div
            className="absolute inset-[2.28%_48.72%_0.57%_11.38%] mask-intersect mask-luminance mask-no-clip mask-no-repeat mask-position-[-0.329px_-0.329px] mask-size-[339.696px_424.216px]"
            style={{ maskImage: `url("${a("a392c545da167073d56a2c3aee992d4e91b316cb")}")` }}
          >
            <div className="absolute inset-[-0.12%_-0.2%]">
              <img
                alt=""
                className="block max-w-none size-full"
                src={a("3f19bc0a139716aa4de151be5c37038148921ba2")}
              />
            </div>
          </div>
        </div>
        <div className="absolute inset-[54.42%_9.32%_22.68%_70.95%]">
          <img
            alt=""
            className="absolute block inset-0 max-w-none size-full"
            src={a("58f4d46e0e206c4ea9c6b35a3c3d420309cbfe6b")}
          />
        </div>
        <div className="absolute inset-[60.06%_10.17%_28.32%_78.25%]">
          <img
            alt=""
            className="absolute block inset-0 max-w-none size-full"
            src={a("ce8f7a95ade2b960a6cf63556ae4f67575f96972")}
          />
        </div>
        <div className="absolute inset-[9.1%_16%_45.58%_53.34%]">
          <img
            alt=""
            className="absolute block inset-0 max-w-none size-full"
            src={a("1201dcfc1330d380f0f531dd75d3a68becd4e81e")}
          />
        </div>
        <div className="absolute inset-[2.31%_8.99%_90.33%_86.03%]">
          <div className="absolute inset-[-4.67%_-4.72%_0_0]">
            <img
              alt=""
              className="block max-w-none size-full"
              src={a("420a9f6a178fb75cbbce324884eca9b47afecca8")}
            />
          </div>
        </div>
        <div className="absolute inset-[0.08%_10.05%_92.56%_84.97%]">
          <div className="absolute inset-[-1.56%_-1.57%_0_0]">
            <img
              alt=""
              className="block max-w-none size-full"
              src={a("8588771ac1cad511069bff72649924faba2412c7")}
            />
          </div>
        </div>
        {/* Small gear (rotates counter-clockwise, faster — Figma keyframes 0° → -35° → -45°) */}
        <div className="qa-gear-sm absolute inset-[30.16%_73.48%_54.34%_15.92%]">
          <img
            alt=""
            className="absolute block inset-0 max-w-none size-full"
            src={a("0e21083f25285e99477d103efabf6906e2111746")}
          />
        </div>
        <div className="qa-gear-sm absolute inset-[30.07%_73.42%_54.25%_15.86%]">
          <img
            alt=""
            className="absolute block inset-0 max-w-none size-full"
            src={a("4886a549750ff9bef93d369be1dad6a993470916")}
          />
        </div>
        {/* Big gear (rotates counter-clockwise, slower — Figma keyframe -20°) */}
        <div className="qa-gear-lg absolute inset-[5.13%_64.69%_67.86%_16.83%]">
          <img
            alt=""
            className="absolute block inset-0 max-w-none size-full"
            src={a("3d5ba22e3494564c10625830b746056cddb73032")}
          />
        </div>
        <div className="qa-gear-lg absolute inset-[5.05%_64.64%_67.78%_16.78%]">
          <img
            alt=""
            className="absolute block inset-0 max-w-none size-full"
            src={a("0a747c6f6c6cff0a04d46942efc8fc1b934d6833")}
          />
        </div>
        <div className="absolute contents inset-[8.8%_15.47%_0.76%_22.68%]">
          <div className="absolute contents inset-[54.42%_8.54%_3%_53.33%]">
            <div
              className="absolute inset-[54.42%_8.54%_3%_53.33%] mask-intersect mask-luminance mask-no-clip mask-no-repeat mask-position-[-195.559px_-198.914px] mask-size-[394.643px_394.344px] mix-blend-color-dodge"
              style={{ maskImage: `url("${a("d9838675cac7d42338ac2a8ea7f1d4cf8743c9a3")}")` }}
            >
              <div className="absolute inset-[-1.35%_-1.03%]">
                <img
                  alt=""
                  className="block max-w-none size-full"
                  src={a("498f951ffff3dddb357fc291b90012b089e4143f")}
                />
              </div>
            </div>
          </div>
        </div>
        <div className="absolute inset-[56.9%_6.54%_-0.01%_54.96%]">
          <img
            alt=""
            className="absolute block inset-0 max-w-none size-full"
            src={a("eb4c5d4b05b87359aec242cb84a9f71b990e599d")}
          />
        </div>
        <div className="absolute inset-[8.8%_15.47%_0.76%_22.68%]">
          <img
            alt=""
            className="absolute block inset-0 max-w-none size-full"
            src={a("d14af8ccf6c10059e8ad8b1e4536b4d04335ad63")}
          />
        </div>
        <div className="absolute inset-[9.21%_16.06%_62.81%_62.69%]">
          <div className="absolute inset-[-0.81%_-0.68%_-0.3%_-0.13%]">
            <img
              alt=""
              className="block max-w-none size-full"
              src={a("2ee2cbb14cc7ec5d4360258fe945e9ee2912c35a")}
            />
          </div>
        </div>
        <div className="absolute contents inset-[8.8%_15.47%_0.76%_22.68%]">
          <div className="absolute contents inset-[54.42%_8.54%_3%_53.33%]">
            <div
              className="absolute inset-[54.42%_8.54%_3%_53.33%] mask-intersect mask-luminance mask-no-clip mask-no-repeat mask-position-[-195.559px_-198.914px] mask-size-[394.643px_394.344px] mix-blend-color-dodge"
              style={{ maskImage: `url("${a("d9838675cac7d42338ac2a8ea7f1d4cf8743c9a3")}")` }}
            >
              <div className="absolute inset-[-1.35%_-1.03%]">
                <img
                  alt=""
                  className="block max-w-none size-full"
                  src={a("7bc6ebccaced3c5a3f77287a01d7e674a827b904")}
                />
              </div>
            </div>
          </div>
        </div>
        <div className="absolute inset-[21.43%_89.52%_77.15%_6.88%]">
          <img
            alt=""
            className="absolute block inset-0 max-w-none size-full"
            src={a("fd1ef841c61f6f8d6ec5bc613799c5bacbd65c1c")}
          />
        </div>
        <div className="absolute inset-[2.75%_89.52%_95.83%_6.88%]">
          <img
            alt=""
            className="absolute block inset-0 max-w-none size-full"
            src={a("9455d89bab7963c942f4466975aa33176ad22393")}
          />
        </div>
        <div className="absolute inset-[12.09%_89.52%_86.49%_6.88%]">
          <img
            alt=""
            className="absolute block inset-0 max-w-none size-full"
            src={a("fd1ef841c61f6f8d6ec5bc613799c5bacbd65c1c")}
          />
        </div>
        <div className="absolute inset-[30.77%_89.52%_67.81%_6.88%]">
          <img
            alt=""
            className="absolute block inset-0 max-w-none size-full"
            src={a("fd1ef841c61f6f8d6ec5bc613799c5bacbd65c1c")}
          />
        </div>
        <div className="absolute inset-[82.87%_0_15%_95.81%]">
          <img
            alt=""
            className="absolute block inset-0 max-w-none size-full"
            src={a("bbebd772672c415b4423a15b6e9c104d390be5f8")}
          />
        </div>
        <div className="absolute inset-[54.81%_0_43.06%_95.81%]">
          <img
            alt=""
            className="absolute block inset-0 max-w-none size-full"
            src={a("ae0ab184d50e9f41fcf47345eaf79d27e3f7d359")}
          />
        </div>
        <div className="absolute inset-[68.84%_0_29.03%_95.81%]">
          <img
            alt=""
            className="absolute block inset-0 max-w-none size-full"
            src={a("bbebd772672c415b4423a15b6e9c104d390be5f8")}
          />
        </div>
        <div className="absolute inset-[96.9%_0_0.97%_95.81%]">
          <img
            alt=""
            className="absolute block inset-0 max-w-none size-full"
            src={a("bbebd772672c415b4423a15b6e9c104d390be5f8")}
          />
        </div>
        <div className="absolute inset-[74.05%_42.3%_0.61%_0]">
          <img
            alt=""
            className="absolute block inset-0 max-w-none size-full"
            src={a("4da3329cc55a3b27ab4fc79b4a9efe09025bc5c7")}
          />
        </div>
        {/* Status dots — pulse white -> red -> white (Figma Default vs Variant2) */}
        <div className="absolute inset-[22.74%_5.29%_57.67%_91.31%]">
          <div className="absolute inset-[-0.59%_-2.31%]">
            <img
              alt=""
              className="block max-w-none size-full"
              src={a("539516e13faf2c5b53a7034caa9d9a3bd66cca9b")}
            />
            <img
              alt=""
              className="qa-dots-pulse absolute inset-0 block max-w-none size-full"
              src={a("0c7deab50018246759700dc338cf81e83cc4af3e")}
            />
          </div>
        </div>
        {/* Pie chart (rotates) */}
        <div className="qa-pie absolute inset-[51.13%_77.23%_28.1%_8.56%]">
          <div className="absolute inset-[-2.21%]">
            <img
              alt=""
              className="block max-w-none size-full"
              src={a("2e2c303e91e00bf27dee9fc82f513c4ad60da185")}
            />
          </div>
        </div>
        <div className="absolute inset-[5.23%_48.57%_75.24%_39.59%]">
          <div className="absolute inset-[-0.59%_-0.66%]">
            <img
              alt=""
              className="block max-w-none size-full"
              src={a("d10910bdedcb76921606e8b538b8aef866b0c069")}
            />
          </div>
        </div>
        <div className="absolute inset-[27.07%_61.68%_53.13%_21.44%]">
          <div className="absolute inset-[-0.58%_-0.46%]">
            <img
              alt=""
              className="block max-w-none size-full"
              src={a("4425dc4f005d9e9e92c235e23fc94b7851f3fc20")}
            />
          </div>
        </div>
        <div className="absolute inset-[9.29%_5.92%_80.62%_87.17%]">
          <img
            alt=""
            className="absolute block inset-0 max-w-none size-full"
            src={a("e49a48364776954fe12356d264c71e6845064289")}
          />
        </div>
        <div className="absolute contents inset-[9.31%_7.59%_45.28%_53.17%]">
          <div
            className="absolute inset-[27.5%_9.62%_45.3%_53.33%] mask-intersect mask-luminance mask-no-clip mask-no-repeat mask-position-[-0.98px_-79.342px] mask-size-[250.351px_197.986px]"
            style={{ maskImage: `url("${a("38100cd962fd6b50733ead77985ad8116f099515")}")` }}
          >
            <div className="absolute inset-[-0.34%_-0.34%_-0.33%_-0.34%]">
              <img
                alt=""
                className="block max-w-none size-full"
                src={a("0f0ffa7c42a5678f2bdf36544ceda841038e0c3d")}
              />
            </div>
          </div>
        </div>
        <div className="absolute inset-[11.18%_61.49%_73.72%_28.18%]">
          <div className="absolute inset-[-0.76%]">
            <img
              alt=""
              className="block max-w-none size-full"
              src={a("94776f7ac3057964daddfd17abd6495c8c046016")}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
